import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  UserPlus,
  User,
  X,
  CreditCard,
  FileText,
  AlertCircle,
  CheckCircle,
  Trash2,
  ExternalLink,
  Copy,
  Loader2
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

const FamilyMembersManager = ({ onPersonsChange }) => {
  const [loading, setLoading] = useState(true);
  const [familyData, setFamilyData] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [newMember, setNewMember] = useState({ name: "", relationship: "spouse" });
  const [paymentLink, setPaymentLink] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const relationships = [
    { value: "spouse", label: "Esposo(a)" },
    { value: "child", label: "Hijo(a)" },
    { value: "parent", label: "Padre/Madre" },
    { value: "sibling", label: "Hermano(a)" },
    { value: "other", label: "Otro" }
  ];

  const fetchFamilyData = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/client/family-members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFamilyData(res.data);
      onPersonsChange?.(res.data);
    } catch (error) {
      console.error("Error fetching family data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFamilyData();
  }, []);

  const handleAddMember = async () => {
    if (!newMember.name.trim()) {
      toast.error("Ingresa el nombre");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/api/client/family-members`,
        newMember,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message);
      setShowAddModal(false);
      setNewMember({ name: "", relationship: "spouse" });
      fetchFamilyData();
    } catch (error) {
      if (error.response?.status === 400) {
        // Need to pay for additional person
        setShowAddModal(false);
        setShowPaymentModal(true);
      } else {
        toast.error(error.response?.data?.detail || "Error al agregar familiar");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestAdditionalPerson = async () => {
    if (!newMember.name.trim()) {
      toast.error("Ingresa el nombre");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/api/client/request-additional-person`,
        newMember,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPaymentLink(res.data);
      toast.success("Link de pago generado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar link");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!confirm(`¿Estás seguro de eliminar a ${memberName}?`)) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/client/family-members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Familiar eliminado");
      fetchFamilyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copiado");
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-navy-100 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 text-navy-400 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-navy-100 overflow-hidden" data-testid="family-members-container">
      {/* Header */}
      <div className="px-5 py-4 border-b border-navy-100 bg-gradient-to-r from-navy-50 to-blue-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-navy-900">Personas en tu Caso</h3>
              <p className="text-sm text-navy-500" data-testid="family-members-count">
                {familyData?.total_persons_added || 1} de {familyData?.authorized_persons || 1} autorizadas
              </p>
            </div>
          </div>
          
          {familyData?.can_add_more ? (
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="add-family-member-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Agregar
            </Button>
          ) : (
            <Button
              onClick={() => setShowPaymentModal(true)}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="add-person-payment-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Agregar Persona (+pago)
            </Button>
          )}
        </div>
      </div>

      {/* Persons List */}
      <div className="p-5 space-y-3" data-testid="family-members-list">
        {/* Main Applicant */}
        <div className="flex items-center justify-between p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="main-applicant">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="font-medium text-navy-900" data-testid="main-applicant-name">{familyData?.main_applicant?.name}</p>
              <p className="text-sm text-emerald-600">Solicitante Principal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full flex items-center gap-1" data-testid="main-applicant-docs">
              <FileText className="w-3 h-3" />
              {familyData?.main_applicant?.documents_count || 0} docs
            </span>
          </div>
        </div>

        {/* Family Members */}
        {familyData?.family_members?.map((member) => (
          <div
            key={member.id}
            className="flex items-center justify-between p-4 bg-navy-50 border border-navy-100 rounded-lg"
            data-testid={`family-member-${member.id}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-navy-900" data-testid="family-member-name">{member.name}</p>
                <p className="text-sm text-navy-500 capitalize" data-testid="family-member-relationship">
                  {relationships.find(r => r.value === member.relationship)?.label || member.relationship}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1" data-testid="family-member-docs">
                <FileText className="w-3 h-3" />
                {member.documents_count || 0} docs
              </span>
              {member.documents_count === 0 && (
                <button
                  onClick={() => handleRemoveMember(member.id, member.name)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid={`delete-member-${member.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Empty Slots */}
        {familyData?.slots_available > 0 && (
          <div className="flex items-center justify-center p-4 border-2 border-dashed border-navy-200 rounded-lg" data-testid="slots-available">
            <p className="text-sm text-navy-400">
              {familyData.slots_available} espacio(s) disponible(s) para agregar familiares
            </p>
          </div>
        )}

        {/* Pending Payment Links */}
        {familyData?.pending_person_links?.length > 0 && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="pending-payment-links">
            <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Personas pendientes de pago
            </h4>
            {familyData.pending_person_links.map((link) => (
              <div key={link.id} className="flex items-center justify-between mt-2 p-2 bg-white rounded border border-amber-100" data-testid={`pending-link-${link.id}`}>
                <span className="text-sm text-navy-700">{link.additional_person_name}</span>
                <span className="text-sm font-medium text-amber-600">${link.amount} USD</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            data-testid="add-member-modal"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-navy-100">
                <h3 className="font-semibold text-navy-900">Agregar Familiar</h3>
                <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-navy-100 rounded" data-testid="close-add-modal">
                  <X className="w-5 h-5 text-navy-500" />
                </button>
              </div>
              
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1">Nombre completo</label>
                  <Input
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    placeholder="Nombre del familiar"
                    data-testid="member-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1">Relación</label>
                  <select
                    value={newMember.relationship}
                    onChange={(e) => setNewMember({ ...newMember, relationship: e.target.value })}
                    className="w-full px-3 py-2 border border-navy-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="member-relationship-select"
                  >
                    {relationships.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-700">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Tienes {familyData?.slots_available} espacio(s) disponible(s)
                  </p>
                </div>
              </div>
              
              <div className="px-5 pb-5 flex gap-3">
                <Button variant="outline" onClick={() => setShowAddModal(false)} className="flex-1" data-testid="cancel-add-member">
                  Cancelar
                </Button>
                <Button
                  onClick={handleAddMember}
                  disabled={submitting}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-add-member"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Agregar
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Request Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            data-testid="payment-modal"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-navy-100 bg-gradient-to-r from-amber-50 to-orange-50">
                <h3 className="font-semibold text-navy-900">Agregar Persona Adicional</h3>
                <button onClick={() => { setShowPaymentModal(false); setPaymentLink(null); }} className="p-1 hover:bg-amber-100 rounded" data-testid="close-payment-modal">
                  <X className="w-5 h-5 text-navy-500" />
                </button>
              </div>
              
              <div className="p-5">
                {!paymentLink ? (
                  <div className="space-y-4" data-testid="payment-form">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-700">
                        <AlertCircle className="w-4 h-4 inline mr-1" />
                        Tu plan incluye <strong>{familyData?.authorized_persons}</strong> persona(s). 
                        Para agregar más, se requiere un pago adicional.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-700 mb-1">Nombre de la persona</label>
                      <Input
                        value={newMember.name}
                        onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                        placeholder="Nombre completo"
                        data-testid="payment-person-name-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-navy-700 mb-1">Relación</label>
                      <select
                        value={newMember.relationship}
                        onChange={(e) => setNewMember({ ...newMember, relationship: e.target.value })}
                        className="w-full px-3 py-2 border border-navy-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                        data-testid="payment-relationship-select"
                      >
                        {relationships.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setShowPaymentModal(false)} className="flex-1" data-testid="cancel-payment">
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleRequestAdditionalPerson}
                        disabled={submitting || !newMember.name.trim()}
                        className="flex-1 bg-amber-600 hover:bg-amber-700"
                        data-testid="generate-payment-link-btn"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                        Generar Link de Pago
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="payment-link-generated">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                      <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                      <h4 className="font-semibold text-emerald-800">¡Link generado!</h4>
                      <p className="text-sm text-emerald-700 mt-1" data-testid="payment-person-name">
                        Completa el pago para agregar a <strong>{paymentLink.person_name}</strong>
                      </p>
                    </div>

                    <div className="bg-navy-50 rounded-lg p-4">
                      <p className="text-sm text-navy-500 mb-1">Monto a pagar:</p>
                      <p className="text-2xl font-bold text-navy-900" data-testid="payment-amount">${paymentLink.amount} USD</p>
                    </div>

                    <div className="flex gap-2">
                      <Input value={paymentLink.payment_link} readOnly className="text-sm bg-navy-50" data-testid="payment-link-url" />
                      <Button variant="outline" onClick={() => copyLink(paymentLink.payment_link)} data-testid="copy-payment-link">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>

                    <Button
                      onClick={() => window.open(paymentLink.payment_link, '_blank')}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="go-to-pay-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Ir a Pagar
                    </Button>

                    <p className="text-xs text-center text-navy-400">
                      Después del pago, podrás subir documentos de {paymentLink.person_name}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FamilyMembersManager;
